/* eslint-disable no-unused-vars */
import './style.scss';

import { Fieldset } from 'primereact/fieldset';
import PropTypes from 'prop-types';
import { Button } from 'primereact/button';
import { useTranslation } from 'react-i18next';
import { useMemo, useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { getRegisterResult } from 'apis/registerResult';
import { getProjectById } from 'apis/project.api';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { toast } from 'layout';
import { TOAST } from 'constant';

import Loading from 'components/Loading';
import { Dialog } from 'primereact/dialog';
import { Message } from 'primereact/message';
import {
  useDownloadRegisterResultFileUpload,
  useGetRegisterResult,
  useGetRegisterResultReviews,
  useMutationCreateRegisterResult,
  useMutationSubmitRegisterResult,
  useMutationUpdateRegisterResult,
  useSendReviewFeedback,
} from 'utils/hooks/registerResult';
import { TextareaInput } from 'components/FormControl';
import GeneralInfo from './GeneralInfo';
import ReviewForm from './ReviewForm';
import RegisterContent from './RegisterContent';
import ExpenditureAndAppendix from './ExpenditureAndAppendix';

export default function RegisterResultForm({
  handleSubmit,
  control,
  reset,
  setValue,
  getValues,
  errors,
  tab,
  setTab,
  isDirty,
  dirtyFields,
  checkValidation,
  isLiquidation,
}) {
  const { t } = useTranslation();
  const { projectId, registerResultId } = useParams();

  const [searchParams] = useSearchParams();
  const reviewId = searchParams.get('reviewId');
  const maxTab = isLiquidation ? 2 : 3;

  const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false);
  const [registerResultIdUsed, setRegisterResultIdUsed] = useState(registerResultId);
  const [isFeedback, setIsFeedback] = useState(false);

  // # Region data

  // data project
  const { data: _dataProject } = useQuery({
    queryKey: ['dataProject'],
    queryFn: () => getProjectById(projectId),
  });
  const dataProject = useMemo(() => _dataProject?.data ?? {}, [_dataProject]);

  const { data: registerResult, refetch } = useGetRegisterResult(projectId, {
    enabled: !!registerResultIdUsed,
  });

  const { data: reviewsData, refetch: refetchReviews } = useGetRegisterResultReviews(
    registerResultId,
    {
      enabled: !!registerResultIdUsed,
    }
  );

  const review = useMemo(() => {
    if (reviewId) {
      return reviewsData?.find((item) => item.id === reviewId);
    }
    return null;
  }, [reviewsData, reviewId]);

  const { mutateAsync, isLoading: isCreateLoading } = useMutationCreateRegisterResult();
  const { mutateAsync: mutateAsyncUpdateRegisterResult } = useMutationUpdateRegisterResult();
  const { mutateAsync: mutateSubmitRegisterResult } = useMutationSubmitRegisterResult();
  const { mutateAsync: mutateDownloadFile } = useDownloadRegisterResultFileUpload();
  const { mutateAsync: sendFeedback } = useSendReviewFeedback();

  // # End region data

  // # Region Event
  const onSubmit = async (data, isSaved = true) => {
    if (!isSaved || (isDirty && Object.keys(dirtyFields).length)) {
      const dataSender = {
        projectId,
        securitySeverity: data?.securitySeverity || 'public',
        settlementAmount: data?.settlementAmount ?? 0,
        savingsAmount: data?.savingsAmount ?? 0,
        recoveryAmount: data?.recoveryAmount ?? 0,
        note: data?.note ?? '',
      };

      const registerResultStats = [];
      const {
        summaryReportQuantity,
        branchProposalReportQuantity,
        thematicReportQuantity,
        mapQuantity,
        filmQuantity,
        videoQuantity,
        imageQuantity,
        cdDiskQuantity,
        documentQuantity,
        other,
      } = data;

      const items = {
        summaryReportQuantity,
        branchProposalReportQuantity,
        thematicReportQuantity,
        mapQuantity,
        filmQuantity,
        videoQuantity,
        imageQuantity,
        cdDiskQuantity,
        documentQuantity,
        other,
      };

      Object.keys(items).forEach((key) => {
        if (items[key]) {
          registerResultStats.push({
            key,
            value: items[key]?.value || '',
          });
        }
      });

      dataSender.registerResultStats = registerResultStats;

      if (!registerResultIdUsed) {
        const result = await mutateAsync(dataSender);
        if (result?.status === 201) {
          refetch();
          if (isSaved) {
            reset({}, { keepValues: true });
            toast(TOAST.SUCCESS, t('r09.action.createSuccess'));
          }
          setRegisterResultIdUsed(result?.data?.id);
          return result?.data?.id;
        }
        if (isSaved) {
          toast(TOAST.ERROR, t('r09.action.createError'));
        }
      } else {
        const result = await mutateAsyncUpdateRegisterResult({
          id: registerResultIdUsed,
          data: dataSender,
        });
        if (result?.data?.success) {
          refetch();
          reset({}, { keepValues: true });
          toast(TOAST.SUCCESS, t('r09.action.updateSuccess'));
        } else {
          toast(TOAST.ERROR, t('r09.action.updateError'));
        }
        // error
      }
    }
    return null;
  };

  // list authors default

  useEffect(() => {
    if (registerResult) {
      // SecurtityInformation
      setValue('securitySeverity', registerResult?.securitySeverity ?? '');

      // Product
      setValue('summaryReportQuantity.value', registerResult?.statistics?.summaryReportQuantity);
      setValue(
        'branchProposalReportQuantity.value',
        registerResult?.statistics?.branchProposalReportQuantity
      );
      setValue('thematicReportQuantity.value', registerResult?.statistics?.thematicReportQuantity);
      setValue('mapQuantity.value', registerResult?.statistics?.mapQuantity);
      setValue('filmQuantity.value', registerResult?.statistics?.filmQuantity);
      setValue('videoQuantity.value', registerResult?.statistics?.videoQuantity);
      setValue('imageQuantity.value', registerResult?.statistics?.imageQuantity);
      setValue('cdDiskQuantity.value', registerResult?.statistics?.cdDiskQuantity);
      setValue('documentQuantity.value', registerResult?.statistics?.documentQuantity);
      setValue('other.value', registerResult?.statistics?.other);

      // Product Description
      setValue('registerResultStats', registerResult?.products ?? []);

      setValue('note', registerResult?.note ?? '');
      setValue('settlementAmount', registerResult?.settlementAmount ?? 0);
      setValue('savingsAmount', registerResult?.savingsAmount ?? 0);
      setValue('recoveryAmount', registerResult?.recoveryAmount ?? 0);
    }
  }, [registerResult]);

  const handleSubmitRegisterResult = async () => {
    if (review?.editable) {
      const { feedback } = getValues();
      if (!feedback || !feedback?.trim()?.length) {
        toast(TOAST.ERROR, 'Vui lòng nhập thông tin phản hồi');
        return;
      }
      await sendFeedback({
        registerResultId: registerResult.id,
        reviewId: review.id,
        body: { feedback },
      });
      toast(TOAST.SUCCESS, 'Gửi phản hồi thành công');
      setIsFeedback(true);
      refetch();
      refetchReviews();
      setIsSubmitModalOpen(false);
    } else {
      const result = await mutateSubmitRegisterResult(registerResult?.id);
      if (result?.status === 200) {
        toast(TOAST.SUCCESS, t('r09.action.submitSuccess'));
        refetch();
        setIsSubmitModalOpen(false);
      } else {
        toast(TOAST.ERROR, t('r09.action.submitError'));
      }
    }
  };

  const footerConfirmationSubmit = () => (
    <div>
      <Button
        label={t('projectProposal.cancel')}
        className="w-7rem"
        severity="danger"
        onClick={() => setIsSubmitModalOpen(false)}
        autoFocus
      />
      <Button
        label={t('projectProposal.ok')}
        className="w-7rem"
        severity="info"
        onClick={handleSubmitRegisterResult}
      />
    </div>
  );

  const handleTabChange = async () => {
    setTab((prev) => prev + 1);
  };

  const handleDownloadFile = async (filename, type) => {
    try {
      await mutateDownloadFile({
        registerResultId: registerResult?.id,
        filename,
        type,
      });
    } catch (error) {
      toast(TOAST.ERROR, t('r09.action.downloadError'));
    }
  };
  // # End region event

  return (
    <div className="project-proposal-form m-0 bg-white p-5 p-fluid">
      <form onSubmit={handleSubmit(onSubmit)} autoComplete="off" className="form-midterm-container">
        <Fieldset className="border-none p-0 p-fluid">
          <div className={tab !== 0 ? 'hidden' : ''}>
            <GeneralInfo data={dataProject} isLiquidation={isLiquidation} />
          </div>
          <div className={tab !== 1 ? 'hidden' : ''}>
            <RegisterContent
              disabled={
                (registerResult?.status && registerResult?.status !== 'draft' && !reviewId) ||
                isFeedback
              }
              control={control}
              errors={errors}
              participants={dataProject?.projectProposal?.projectProposalContacts ?? []}
              onSubmit={() => onSubmit(getValues(), false)}
              registerResultId={registerResultIdUsed}
              refetch={refetch}
              handleDownloadFile={handleDownloadFile}
              isLiquidation={isLiquidation}
            />
          </div>
          <div className={tab !== maxTab - 1 ? 'hidden' : ''}>
            <ExpenditureAndAppendix
              control={control}
              errors={errors}
              registerResultId={registerResultIdUsed}
              refetch={refetch}
              minuteFile={registerResult?.minuteFile}
              appendixeArray={registerResult?.appendixes || []}
              handleDownloadFile={handleDownloadFile}
              isLiquidation={isLiquidation}
              disabled={
                (registerResult?.status && registerResult?.status !== 'draft' && !reviewId) ||
                isFeedback
              }
              onSubmit={() => onSubmit(getValues(), false)}
            />
          </div>
        </Fieldset>
        <div className="flex justify-content-end mt-2">
          {tab !== 0 && tab !== maxTab && !registerResult?.isSubmitted && (
            <Button
              type="submit"
              className="btn-control font-semibold mr-3"
              severity="success"
              onClick={checkValidation}
              disabled={!(isDirty && Object.keys(dirtyFields).length) || isCreateLoading}
            >
              {t('formLayout.save')}
            </Button>
          )}
          {tab !== maxTab && (
            <Button
              type="button"
              className="p-button-next btn-control"
              onClick={() => {
                if (tab === 0) {
                  setTab((prev) => prev + 1);
                } else if (!checkValidation()) {
                  handleTabChange();
                  onSubmit(getValues(), true);
                }
              }}
              disabled={isCreateLoading}
            >
              {t('formLayout.next')}
            </Button>
          )}
        </div>
      </form>
      <div className={tab !== maxTab ? 'hidden' : 'ml-4'}>
        <ReviewForm
          data={dataProject}
          handleDownloadFile={handleDownloadFile}
          isLiquidation={isLiquidation}
          registerResultIdUsed={registerResultIdUsed}
        />
        <div className="flex justify-content-end mt-6">
          <Link to="/project">
            <Button type="button" className="btn-submit  font-semibold mr-3" severity="danger">
              {t('projectProposal.cancel')}
            </Button>
          </Link>
          {!registerResult?.isSubmitted && (
            <Button
              type="button"
              className="btn-submit font-semibold mr-3"
              severity="success"
              onClick={() => setIsSubmitModalOpen(true)}
              disabled={
                (registerResult?.status !== 'draft' && !reviewId) || !registerResult || isFeedback
              }
            >
              {t('r09.submit')}
            </Button>
          )}
        </div>
      </div>
      <Dialog
        header={<p className="text-blue-500">{t('projectProposal.confirmationTitle')}</p>}
        visible={isSubmitModalOpen}
        position="center"
        style={{ width: '400px' }}
        onHide={() => setIsSubmitModalOpen(false)}
        draggable={false}
        footer={footerConfirmationSubmit}
      >
        {review?.editable && (
          <div className="p-fluid">
            <TextareaInput
              control={control}
              errors={errors}
              name="feedback"
              row={4}
              label="Thông tin phản hồi"
              isRequired
            />
          </div>
        )}
        <Message className="mt-4 w-full" severity="warn" text={t('r09.confirm')} />
      </Dialog>
    </div>
  );
}

RegisterResultForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
  getValues: PropTypes.func.isRequired,
  reset: PropTypes.func.isRequired,
  control: PropTypes.shape({}).isRequired,
  errors: PropTypes.shape({}).isRequired,
  tab: PropTypes.number,
  setTab: PropTypes.func,
  isDirty: PropTypes.bool.isRequired,
  dirtyFields: PropTypes.shape({}).isRequired,
  checkValidation: PropTypes.func.isRequired,
  isLiquidation: PropTypes.bool,
};

RegisterResultForm.defaultProps = {
  tab: 0,
  setTab: () => null,
  isLiquidation: false,
};
